import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type CategoryDetailResponse,
  type CategoryItem,
  type CreateCategoryInput,
  categoryApis,
} from '../../../gateways/breadstack/categoryApis'
import { posClassificationApis } from '../../../gateways/breadstack/posClassificationApis'
import type { CategoryMappingDict, WebCategory } from '../types'
import { STORE_TYPES } from '../utils/storeTypes'
import {
  type POSClassificationsResult,
  buildWebCategoryTree,
  mapGreenlineClassifications,
  mapPOSClassifications,
  sumPosTotalProducts,
} from './categoryMappers'

// Pantry-features query key for the flat category list.
export const categoryListQueryKey = (storeId: string) =>
  ['categories', storeId] as const

// Legacy app query key (frontend/src/pages/Category/PantryCategory/hooks.ts uses
// `pantry-web-categories`). When pantry-features and the legacy CategoryList page
// are mounted concurrently (mid-migration), we want a single source of truth, so
// every mutation invalidates BOTH keys on success.
export const legacyCategoryListQueryKey = (storeId: string) =>
  ['pantry-web-categories', storeId] as const

export const useCategoryList = (storeId: string) =>
  useQuery<CategoryItem[]>({
    queryKey: categoryListQueryKey(storeId),
    queryFn: () => categoryApis.getList(storeId),
    enabled: Boolean(storeId),
    staleTime: 5 * 60 * 1000,
  })

// Single-record fetch used by the drawer to source fresh field values when a
// category is opened for editing. Replaces the previous behaviour of seeding
// the form from the list cache (which can lag behind PUTs and is missing
// optional fields like `headline` / `seo` on legacy rows that haven't been
// re-fetched since they last changed). Cached per-(storeId, categoryId) so
// re-opening the same row is instant; invalidated alongside the list keys on
// every mutation via `invalidateCategoryLists`.
export const categoryDetailQueryKey = (
  storeId: string,
  categoryId: string | null | undefined,
) => ['pantry-category-detail', storeId, categoryId ?? ''] as const

export const useCategoryDetail = (
  storeId: string,
  categoryId: string | null | undefined,
) =>
  useQuery<CategoryDetailResponse | null>({
    queryKey: categoryDetailQueryKey(storeId, categoryId),
    queryFn: () => categoryApis.getDetail(storeId, String(categoryId ?? '')),
    enabled: Boolean(storeId && categoryId),
    staleTime: 5 * 60 * 1000,
  })

// Tree-shaped query consumed by the Pantry Category page. Hits the richer
// `readWebCategoriesWithPOS` endpoint so cova_category / greenline_category /
// pos_mapping metadata is preserved, then runs the payload through
// buildWebCategoryTree() to produce the WebCategory[] structure the UI needs.
export const useWebCategoriesWithPOS = (storeId: string) =>
  useQuery<WebCategory[]>({
    queryKey: legacyCategoryListQueryKey(storeId),
    queryFn: async () => {
      const items = await categoryApis.readWebCategoriesWithPOS(storeId)
      return buildWebCategoryTree(items)
    },
    enabled: Boolean(storeId),
    staleTime: 5 * 60 * 1000,
  })

// Query key for the unified many-to-many mapping endpoint
// (GET /pantry/stores/:storeId/category-mapping). Owned by this module —
// invalidated on every mapping save AND alongside the category list keys so
// the Mapping tab and Web tab stay in lockstep.
export const categoryMappingQueryKey = (storeId: string) =>
  ['pantry-category-mapping', storeId] as const

function invalidateCategoryLists(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string,
) {
  queryClient.invalidateQueries({ queryKey: categoryListQueryKey(storeId) })
  queryClient.invalidateQueries({
    queryKey: legacyCategoryListQueryKey(storeId),
  })
  queryClient.invalidateQueries({
    queryKey: categoryMappingQueryKey(storeId),
  })
  // Detail keys are per-(storeId, categoryId); invalidate all of them under
  // this store so the drawer re-fetches the next time it's opened.
  queryClient.invalidateQueries({
    queryKey: ['pantry-category-detail', storeId],
  })
}

// Source of truth for the Mapping tab. Replaces the legacy derivation that
// walked `cova_category` / `greenline_category` / `pos_mapping` arrays
// embedded on each web category (see CategoryContext linkedPosCategories memo
// pre-2026-06-01). The new endpoint is POS-agnostic — same shape for Cova,
// Greenline, Flowhub, Lightspeed.
export const useCategoryMapping = (storeId: string) =>
  useQuery<CategoryMappingDict>({
    queryKey: categoryMappingQueryKey(storeId),
    queryFn: () => categoryApis.readCategoryMapping(storeId),
    enabled: Boolean(storeId),
    staleTime: 5 * 60 * 1000,
  })

export const useCreateCategory = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation<CategoryItem | null, Error, CreateCategoryInput>({
    // Matches legacy contract verified in
    // frontend/src/pages/Category/PantryCategory/hooks.ts:274-285:
    // POST /stores/:storeId/categories with body { item_data: {...} } via
    // categoryApi.createWebCategory(storeId, itemData).
    mutationFn: (input) => categoryApis.create(storeId, input),
    onSuccess: () => invalidateCategoryLists(queryClient, storeId),
  })
}

export interface UpdateCategoryArgs {
  categoryId: string
  input: CreateCategoryInput
}

export const useUpdateCategory = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, UpdateCategoryArgs>({
    mutationFn: ({ categoryId, input }) =>
      categoryApis.update(storeId, categoryId, input),
    onSuccess: () => invalidateCategoryLists(queryClient, storeId),
  })
}

export const useDeleteCategory = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, string>({
    mutationFn: (categoryId) => categoryApis.delete(storeId, categoryId),
    onSuccess: () => invalidateCategoryLists(queryClient, storeId),
  })
}

// Bulk set `visible` true/false for many categories (PATCH /categories/bulk).
// Mirrors useUpdateCategory's invalidation so the Web tab + Mapping tab refetch.
export interface BulkUpdateVisibilityArgs {
  ids: string[]
  visible: boolean
}

export const useBulkUpdateVisibility = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, BulkUpdateVisibilityArgs>({
    mutationFn: ({ ids, visible }) =>
      categoryApis.bulkUpdateVisible(storeId, ids, visible),
    onSuccess: () => invalidateCategoryLists(queryClient, storeId),
  })
}

// Bulk delete many categories via the existing batch endpoint
// (PUT /categories { data: { delete } }). The backend rejects the whole batch
// with a 400 if any id is POS-mapped, so the mutation rejects and the caller
// surfaces the message without clearing the selection.
export const useBulkDeleteCategories = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, string[]>({
    mutationFn: (ids) => categoryApis.bulkDelete(storeId, ids),
    onSuccess: () => invalidateCategoryLists(queryClient, storeId),
  })
}

// Removes a single active 301 redirect (row in `category_slug_history`) from
// the given category. Backend guards against deleting the row that mirrors
// the current slug, so the only invariant the UI needs to enforce is "don't
// pass an arbitrary string as slugHistoryId" — pass the `id` field from a
// SlugHistoryEntry returned by useCategoryDetail.
export interface DeleteSlugHistoryArgs {
  categoryId: string
  slugHistoryId: string
}

export const useDeleteSlugHistory = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, DeleteSlugHistoryArgs>({
    mutationFn: ({ categoryId, slugHistoryId }) =>
      categoryApis.deleteSlugHistory(storeId, categoryId, slugHistoryId),
    onSuccess: () => invalidateCategoryLists(queryClient, storeId),
  })
}

// `storeType` is required so Cova/Greenline cache entries don't collide if the
// active store changes mid-session. `organizationId` and `companyId` are only
// consumed by the Cova branch — the Greenline branch hits a GET endpoint that
// derives company scope from the `bs-store-id` header.
export interface POSClassificationsPayload {
  storeId: string
  storeType: string
  organizationId?: string
  companyId?: number | string
}

export const posClassificationsQueryKey = (
  payload: POSClassificationsPayload | null,
) =>
  [
    'pantry-pos-classifications',
    payload?.storeType,
    payload?.storeId,
    payload?.organizationId,
    payload?.companyId,
  ] as const

function isPOSClassificationsPayloadEnabled(
  payload: POSClassificationsPayload | null,
): boolean {
  if (!payload?.storeId) return false
  if (payload.storeType.toLowerCase() === STORE_TYPES.GREENLINE) {
    // Greenline only needs storeId — bs-store-id header carries the scope.
    return true
  }
  // Cova (and any unknown POS type that falls through to the Cova branch)
  // still requires the org + numeric company_id.
  return (
    !!payload.organizationId &&
    payload.companyId != null &&
    Number.isFinite(Number(payload.companyId))
  )
}

export const usePOSClassifications = (
  payload: POSClassificationsPayload | null,
) =>
  useQuery<POSClassificationsResult>({
    queryKey: posClassificationsQueryKey(payload),
    queryFn: async () => {
      if (!payload) return { categories: [], totalProducts: 0 }
      if (payload.storeType.toLowerCase() === STORE_TYPES.GREENLINE) {
        const data = await posClassificationApis.getGreenlineClassifications(
          payload.storeId,
        )
        // Total is summed from per-category counts (same derivation as Cova).
        // Greenline category_mapping carries no product counts yet, so each
        // category maps to 0 and the total stays 0 until the Greenline backend
        // emits a per-category count — at which point this becomes correct.
        const categories = mapGreenlineClassifications(data)
        return {
          categories,
          totalProducts: sumPosTotalProducts(categories),
        }
      }
      // Cova fallback (default branch). Preserves today's behaviour for any
      // POS type the dispatcher doesn't yet handle.
      const data = await posClassificationApis.getClassifications({
        store_id: payload.storeId,
        organization_id: payload.organizationId ?? '',
        company_id: Number(payload.companyId ?? 0),
      })
      return mapPOSClassifications(data)
    },
    enabled: isPOSClassificationsPayloadEnabled(payload),
    staleTime: 5 * 60 * 1000,
  })

// Replaces the Cova-specific useSavePOSMappings() that POSTed to
// /cova/pre-onboarding/classifications/update with a {src, dest} payload.
// The new unified PUT endpoint takes the full many-to-many dict directly and
// works for every POS type with no store-type / company_id branching.
export const useSaveCategoryMapping = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, CategoryMappingDict>({
    mutationFn: (mapping) =>
      categoryApis.updateCategoryMapping(storeId, mapping),
    onSuccess: () => {
      invalidateCategoryLists(queryClient, storeId)
    },
  })
}
